import { z } from "zod";

import type { ApprovalFlow } from "../models/approval";

import type { ApprovalAction } from "./approval";
import { TeamMemberGroupZodSchema, type TeamMemberGroup } from "./team-member-group";

// --- Performance review cycle cadence/style ---

export const CadenceValues = ["monthly", "quarterly", "semi_annually", "annually"] as const;
export type Cadence = (typeof CadenceValues)[number];

export const RequestStyleValues = ["start_of_cycle", "anniversary", "start_date", "date_added"] as const;
export type RequestStyle = (typeof RequestStyleValues)[number];

export const ReviewPeriodValues = ["1_week", "2_weeks", "3_weeks"] as const;
export type ReviewPeriod = (typeof ReviewPeriodValues)[number];

// --- Review cycle status ---

export const REVIEW_CYCLE_STATUS_VALUES = [
  "draft",
  "upcoming",
  "in_progress",
  "completed",
  "archived",
] as const;
export type ReviewCycleStatus = (typeof REVIEW_CYCLE_STATUS_VALUES)[number];

// --- Review cycle scheduling ---

export const REVIEW_CYCLE_SCHEDULING_TYPE_VALUES = ["timeline", "tenure"] as const;
export type ReviewCycleSchedulingType = (typeof REVIEW_CYCLE_SCHEDULING_TYPE_VALUES)[number];

// --- Tenure phase start triggers ---

export const TENURE_PHASE_START_TRIGGER_VALUES = ["on_milestone", "offset_from_milestone"] as const;
export type TenurePhaseStartTrigger = (typeof TENURE_PHASE_START_TRIGGER_VALUES)[number];

// --- Manager start triggers ---

export const TENURE_MANAGER_START_TRIGGER_VALUES = [
  "start_of_cycle",
  "after_all_other_reviews",
  "offset_from_start",
] as const;
export type TenureManagerStartTrigger = (typeof TENURE_MANAGER_START_TRIGGER_VALUES)[number];

export const TIMELINE_MANAGER_START_TRIGGER_VALUES = [
  "start_of_cycle",
  "after_all_other_reviews",
  "custom_date",
] as const;
export type TimelineManagerStartTrigger = (typeof TIMELINE_MANAGER_START_TRIGGER_VALUES)[number];

// --- Peer review ---

export const PEER_SELECTION_METHOD_VALUES = [
  "self",
  "admin",
  "direct_manager",
  "crew_lead",
  "department_head",
  "job_supervisor",
] as const;
export type PeerSelectionMethod = (typeof PEER_SELECTION_METHOD_VALUES)[number];

export const FEEDBACK_VISIBILITY_VALUES = [
  "original_with_names",
  "original_anonymous",
  "manager_summary",
] as const;
export type FeedbackVisibility = (typeof FEEDBACK_VISIBILITY_VALUES)[number];

// --- Review cycle config ---

export const REVIEW_CYCLE_CONFIG_STATUS_VALUES = ["draft", "active", "ended", "archived"] as const;
export type ReviewCycleConfigStatus = (typeof REVIEW_CYCLE_CONFIG_STATUS_VALUES)[number];

export const RECURRENCE_VALUES = ["annually", "semi_annually", "quarterly"] as const;
export type Recurrence = (typeof RECURRENCE_VALUES)[number];

// --- Delivery configuration ---

export type PacketContents = {
  include_manager_feedback?: boolean;
  include_self_feedback?: boolean;
  include_peer_feedback?: boolean;
  include_upward_feedback?: boolean;
  include_meeting_notes?: boolean;
};

export type NotificationChannels = {
  email: boolean;
  sms: boolean;
};

export type DeliveryNotificationsConfig = {
  new_review_to_complete: NotificationChannels;
  upcoming_review_deadline: NotificationChannels;
  packet_ready?: NotificationChannels;
  peer_nomination_approved?: NotificationChannels;
};

export type DeliveryConfig = {
  packet_contents: PacketContents;
  require_employee_acknowledgment: boolean;
  notifications: DeliveryNotificationsConfig;
  packet_approval_policy_id: string;
};

// Wire shape used by the wizard, the zod-validated request payloads, and any caller
// that hasn't yet been converted to a persisted DeliveryConfig. The policy id is
// backend-derived (minted from `packet_approval_flow` in `upsertPacketApprovalPolicy`),
// so input shapes cannot supply it.
export type DeliveryConfigInput = Omit<DeliveryConfig, "packet_approval_policy_id"> & {
  packet_approval_flow?: ApprovalFlow;
};

// --- Review display status (unified across dashboard and mobile) ---

export const REVIEW_DISPLAY_STATUS_VALUES = [
  "completed",
  "in_progress",
  "not_started",
  "not_open",
  "overdue",
  // Unlike "overdue", nobody still owes this one: its packet has already shipped.
  "incomplete",
] as const;
export type ReviewDisplayStatus = (typeof REVIEW_DISPLAY_STATUS_VALUES)[number];

// --- Packet status ---

export const PACKET_STATUS_VALUES = [
  "pending_reviews",
  "pending_approval",
  "ready_to_share",
  "shared",
  "acknowledged",
  "closed",
  "cancelled",
] as const;
export type PacketStatus = (typeof PACKET_STATUS_VALUES)[number];

// --- Packet approval status (stored on TMPR, written by the approval service) ---

export const PACKET_APPROVAL_STATUS_VALUES = [
  "unapproved",
  "approved",
] as const satisfies readonly ApprovalAction[];
export type PacketApprovalStatus = (typeof PACKET_APPROVAL_STATUS_VALUES)[number];

// --- Inline form input ---

export type InlineFormInput = {
  name: string;
  description?: string;
  components?: unknown[];
};

// --- Reviewee filter ---

export type RecurringRevieweeFilter = {
  team_member_groups?: TeamMemberGroup[];
  minimum_tenure_days?: number;
};

// --- Phase scheduling (shared across review cycle types) ---

export type PhaseScheduling = {
  timeline?: { start_date: string; end_date: string };
  tenure?: {
    start_trigger?: TenurePhaseStartTrigger;
    start_offset_days?: number;
    duration: number;
  };
};

// --- Review cycle phase configs ---

type BasePhaseConfig = PhaseScheduling & {
  form_id?: string;
  template_id?: string;
};

export type SelfReview = BasePhaseConfig;

export type ManagerReview = Omit<BasePhaseConfig, "timeline" | "tenure"> & {
  timeline?: {
    start_trigger: TimelineManagerStartTrigger;
    start_date?: string;
    end_date: string;
  };
  tenure?: {
    start_trigger: TenureManagerStartTrigger;
    start_offset_days?: number;
    duration: number;
  };
};

export type PeerReview = BasePhaseConfig & {
  // Absent when peer_selection_method is "admin": admins assign peers directly, so there is no
  // nomination phase to schedule, no peer cap, and no nomination approval policy.
  peer_selection?: PhaseScheduling;
  peer_selection_method: PeerSelectionMethod;
  maximum_peers?: number;
  feedback_visibility: FeedbackVisibility;
  nomination_policy_id?: string;
};

export type UpwardReview = BasePhaseConfig & {
  feedback_visibility: FeedbackVisibility;
};

// --- Review cycle phase input types (for inline form creation) ---

type BasePhaseInput = PhaseScheduling & {
  form?: InlineFormInput;
  template_id?: string;
};

export type SelfReviewInput = BasePhaseInput;

export type ManagerReviewInput = Omit<BasePhaseInput, "timeline" | "tenure"> & {
  timeline?: {
    start_trigger: TimelineManagerStartTrigger;
    start_date?: string;
    end_date: string;
  };
  tenure?: {
    start_trigger: TenureManagerStartTrigger;
    start_offset_days?: number;
    duration: number;
  };
};

export type PeerReviewInput = BasePhaseInput & {
  peer_selection?: PhaseScheduling;
  peer_selection_method: PeerSelectionMethod;
  maximum_peers?: number;
  feedback_visibility: FeedbackVisibility;
  nomination_approval_flow?: ApprovalFlow;
};

export type UpwardReviewInput = BasePhaseInput & {
  feedback_visibility: FeedbackVisibility;
};

// --- Review cycle config phase configs (offset-based, for recurring configs) ---

type BaseConfigPhase = {
  form_id?: string;
  template_id?: string;
  start_offset_days: number;
  end_offset_days: number;
};

export type SelfReviewConfig = BaseConfigPhase;

export type ManagerReviewConfig = Omit<BaseConfigPhase, "start_offset_days"> & {
  start_trigger: TimelineManagerStartTrigger;
  start_offset_days?: number;
};

// Admin-selected peers have no nomination step, so the nomination window and policy are absent.
export type PeerReviewConfig = BaseConfigPhase & {
  peer_selection?: { start_offset_days: number; end_offset_days: number };
  peer_selection_method: PeerSelectionMethod;
  maximum_peers?: number;
  feedback_visibility: FeedbackVisibility;
  nomination_policy_id?: string;
};

export type UpwardReviewConfig = BaseConfigPhase & {
  feedback_visibility: FeedbackVisibility;
};

// --- Review cycle config phase input types (for inline form creation) ---

type BaseConfigPhaseInput = {
  form: InlineFormInput;
  template_id?: string;
  start_offset_days: number;
  end_offset_days: number;
};

export type SelfReviewConfigInput = BaseConfigPhaseInput;

export type ManagerReviewConfigInput = Omit<BaseConfigPhaseInput, "start_offset_days"> & {
  start_trigger: TimelineManagerStartTrigger;
  start_offset_days?: number;
};

export type PeerReviewConfigInput = BaseConfigPhaseInput & {
  peer_selection?: { start_offset_days: number; end_offset_days: number };
  peer_selection_method: PeerSelectionMethod;
  maximum_peers?: number;
  feedback_visibility: FeedbackVisibility;
  nomination_approval_flow?: ApprovalFlow;
};

export type UpwardReviewConfigInput = BaseConfigPhaseInput & {
  feedback_visibility: FeedbackVisibility;
};

export const RecurringRevieweeFilterZodSchema = z.object({
  team_member_groups: z.array(TeamMemberGroupZodSchema).optional(),
  minimum_tenure_days: z.number().int().nonnegative().optional(),
});
